/*
 * kitchen.js — Kitchen-closed "back at 9 AM" runtime controller (W2, B2C-186)
 *
 * The OUTER GATE on fresh / kitchen-made surfaces. Loads last in the </body> chain
 * (after gelato.js). When the kitchen is closed it replaces the cart control of every
 * [data-kitchen-fresh] surface (card add button / gelato pick CTA / stepper alike) with
 * a single disabled amber "Back at 9 AM" control, and reveals the PDP banner; when open
 * it makes no change (the server-rendered native control stands). Precedence:
 *   kitchen-closed > gelato pick > native stepper/add > native "Sold out".
 *
 * Time is read client-side (cache-safe) via window.CasinettoKitchen. See
 * decisions/kitchen-closed.md (Q3/Q4/Q5). No new events — only listens to cart:update
 * and re-applies after pcard/gelato/cross-sync have processed it.
 *
 * QA override: ?kitchen=closed / ?kitchen=open forces the state (also honoured by the
 * pre-paint head flag in theme.liquid).
 */
(function () {
  'use strict';

  var K = window.CasinettoKitchen;
  if (!K) { return; } // config missing — fail open (never block ordering)

  var TIME_PLACEHOLDER = '__TIME__';
  var html = document.documentElement;

  // Inline clock glyph (avoids a Liquid render in JS; mirrors pcard's inline SVG pattern).
  var CLOCK_SVG = '<span class="c-icon" aria-hidden="true"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg></span>';

  /* ---- State ---------------------------------------------------- */

  function override() {
    var m = window.location.search.match(/[?&]kitchen=(open|closed)\b/);
    return m ? m[1] : null;
  }

  function isClosedNow() {
    var o = override();
    if (o) { return o === 'closed'; }
    return !K.isOpen();
  }

  /* ---- Amber control builder ------------------------------------ */

  function buildClosedControl(card) {
    var time = K.reopenLabel();
    var labelTpl = card.getAttribute('data-kitchen-label') || ('Back at ' + TIME_PLACEHOLDER);
    var shortTpl = card.getAttribute('data-kitchen-label-short') || TIME_PLACEHOLDER;
    var ariaTpl = card.getAttribute('data-kitchen-aria') || labelTpl;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'c-pcard__closed';
    btn.disabled = true;
    btn.setAttribute('aria-label', ariaTpl.split(TIME_PLACEHOLDER).join(time));
    btn.innerHTML = CLOCK_SVG +
      '<span class="c-pcard__closed-label"></span>' +
      '<span class="c-pcard__closed-label--short"></span>';
    btn.querySelector('.c-pcard__closed-label').textContent = labelTpl.split(TIME_PLACEHOLDER).join(time);
    btn.querySelector('.c-pcard__closed-label--short').textContent = shortTpl.split(TIME_PLACEHOLDER).join(time);
    return btn;
  }

  /* ---- Apply / restore (idempotent) ----------------------------- */

  function applyClosedState() {
    var closed = isClosedNow();
    html.setAttribute('data-kitchen', closed ? 'closed' : 'open');

    var cards = document.querySelectorAll('[data-kitchen-fresh="1"]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var cartDiv = card.querySelector('.c-pcard__cart');
      if (!cartDiv) { continue; }

      if (closed) {
        // Swap once; idempotent — skip if the amber control is already there.
        if (!cartDiv.querySelector('.c-pcard__closed')) {
          if (card._kitchenNativeHTML == null) {
            card._kitchenNativeHTML = cartDiv.innerHTML; // stash native control once
          }
          cartDiv.innerHTML = '';
          cartDiv.appendChild(buildClosedControl(card));
        }
      } else {
        // Open — restore the native control if we previously swapped it.
        if (card._kitchenNativeHTML != null && cartDiv.querySelector('.c-pcard__closed')) {
          cartDiv.innerHTML = card._kitchenNativeHTML;
          card._kitchenNativeHTML = null;
          // qty may have drifted while closed; the next cart:update reconciles it.
        }
      }
    }

    // PDP banner (one per page) — revealed only when closed; __TIME__ filled once.
    var banner = document.querySelector('[data-kitchen-banner]');
    if (banner) {
      banner.hidden = !closed;
      if (closed && banner.getAttribute('data-kitchen-filled') !== '1') {
        var body = banner.querySelector('[data-kitchen-banner-body]');
        if (body) {
          body.textContent = body.textContent.split(TIME_PLACEHOLDER).join(K.reopenLabel());
        }
        banner.setAttribute('data-kitchen-filled', '1');
      }
    }

    html.setAttribute('data-kitchen-resolved', '');
  }

  /* ---- Click guard (capture phase) — FOUC backstop -------------- */

  document.addEventListener('click', function (e) {
    if (!isClosedNow()) { return; }
    if (!e.target.closest) { return; }
    var fresh = e.target.closest('[data-kitchen-fresh="1"]');
    if (!fresh) { return; }
    // Neutralise only the cart-control area (and any gelato opener) — NOT the product
    // link or favourite button, so a closed fresh item is still browsable.
    if (e.target.closest('.c-pcard__cart') || e.target.closest('[data-gelato-open]')) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);

  /* ---- cart:update — re-apply AFTER the native machines ---------- */

  document.addEventListener('cart:update', function () {
    // Defer to the end of the task queue so pcard/gelato/cross-sync re-render first,
    // then kitchen re-suppresses on top (register-late-win).
    setTimeout(applyClosedState, 0);
  });

  /* ---- Boundary timer + visibility backstop --------------------- */

  function msToNextBoundary() {
    var d = K.dubaiNow();
    var nowMin = d.getHours() * 60 + d.getMinutes();
    var secs = d.getSeconds();
    var marks = [K.OPEN_HOUR * 60, K.CLOSE_HOUR * 60, 24 * 60 + K.OPEN_HOUR * 60];
    var nextMin = marks[marks.length - 1];
    for (var i = 0; i < marks.length; i++) {
      if (marks[i] > nowMin) { nextMin = marks[i]; break; }
    }
    return ((nextMin - nowMin) * 60 - secs) * 1000 + 1000; // +1s cushion past the boundary
  }

  var boundaryTimer = null;
  function armBoundary() {
    if (boundaryTimer) { clearTimeout(boundaryTimer); }
    boundaryTimer = setTimeout(function () {
      applyClosedState();
      armBoundary();
    }, msToNextBoundary());
  }

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) { applyClosedState(); }
  });

  /* ---- Init ----------------------------------------------------- */

  // Search surfaces insert compact product cards (with data-kitchen-fresh) AFTER load,
  // so the initial applyClosedState never sees them. Re-apply (idempotent) whenever the
  // subtree of a predictive-search-component, or of any [data-kitchen-observe]
  // container, changes. The hook covers containers outside a predictive-search-component:
  // the mobile overlay's zero-query panel (snippets/search-overlay.liquid, B2C-321).
  // Every target is a permanent element rendered on page load, so the observer lives
  // as long as the page and needs no disconnect (CONSTITUTION §VI.21).
  function observeSearch() {
    if (!('MutationObserver' in window)) { return; }
    var searchEls = document.querySelectorAll('predictive-search-component, [data-kitchen-observe]');
    if (!searchEls.length) { return; }
    var pending = false;
    var mo = new MutationObserver(function () {
      if (pending) { return; }
      pending = true;
      requestAnimationFrame(function () { pending = false; applyClosedState(); });
    });
    for (var i = 0; i < searchEls.length; i++) {
      mo.observe(searchEls[i], { childList: true, subtree: true });
    }
  }

  function init() {
    applyClosedState();
    armBoundary();
    observeSearch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
